/* FlyingSpaghettiMonster.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");

const REGISTRATIONS = require("../appendages/Registrations");
const SectionRouter = require("@gluenetworks/steve_core_section_router");

/**
 * hooks based on path token changes
 * intercept router _s_ changes
 *      execute hook if concerned with source or destination s:token
 *
 * hooks registered under namespace with metadata
 */

class FlyingSpaghettiMonster extends EventEmitter {
    constructor(logger, steve, di, router, userSession /*, muf*/) {
        super();
        this._logger = logger;
        this._steve = steve;
        this._router = router;
        this._userSession = userSession;
        this._di = di;

        this._sectionRouterContainer = document.createElement("squishable-section-router-gl");

        const routeDetails = this._router.getRouteDetails();
        if (
            routeDetails &&
            (routeDetails.s === "LoginCenter" ||
                routeDetails.s === "LogoutCenter" ||
                routeDetails.s === "PasswordReset" ||
                routeDetails.s === "Eula" ||
                routeDetails.s === "Banner")
        ) {
            this._sectionRouterContainer.classList.add("login");
        } else {
            this._sectionRouterContainer.classList.remove("login");
        }

        if (routeDetails && routeDetails.s === "RoboHelp") {
            document.querySelector("body").classList.add("robo-page");
        } else {
            document.querySelector("body").classList.remove("robo-page");
        }

        this._sectionRouter = this._di.create(SectionRouter, {
            injectableOverrides: {
                "$teve.router": this,
                "$teve.core": this,
            },
        });

        this._sectionRouterContainer.appendChild(this._sectionRouter.renderer.rootElement);
        this._di.registry.register("$teve.sectionRouter", this._sectionRouter);
    }

    _registerAllAppendages() {
        this._di.registry.register(
            REGISTRATIONS.Startup,
            require("../appendages/StartupAppendage")
        );
        this._di.registry.register(
            REGISTRATIONS.ReconnectStartup,
            require("../appendages/ReconnectAppendage")
        );
        this._di.registry.register(REGISTRATIONS.Eula, require("../appendages/EulaAppendage"));
        this._di.registry.register(
            REGISTRATIONS.EulaFromUndef,
            require("../appendages/EulaAppendage")
        );
        this._di.registry.register(REGISTRATIONS.EulaToAny, require("../appendages/EulaAppendage"));
        this._di.registry.register(REGISTRATIONS.Banner, require("../appendages/BannerAppendage"));
        this._di.registry.register(
            REGISTRATIONS.BannerFromEula,
            require("../appendages/BannerAppendage")
        );
        this._di.registry.register(
            REGISTRATIONS.BannerToAny,
            require("../appendages/BannerAppendage")
        );
        this._di.registry.register(
            REGISTRATIONS.ShowHomeBar,
            require("../appendages/ShowHomeBarAppendage")
        );
        this._di.registry.register(
            REGISTRATIONS.HideHomeBar,
            require("../appendages/ShowHomeBarAppendage")
        );
        this._di.registry.register(
            REGISTRATIONS.HomeBarFromUndef,
            require("../appendages/ShowHomeBarAppendage")
        );
    }

    _registerListeners() {
        this._router.onPathTokenRollupChange(
            ["app", "s"],
            (this._onRouteInfoChanged = this._onRouteInfoChanged.bind(this))
        );

        this._steve.once(
            "SteveStartupCompleted",
            (this._onSteveStartupCompleted = this._onSteveStartupCompleted.bind(this))
        );
    }

    _unregisterListeners() {}

    _onSteveStartupCompleted(launchOpts) {
        let defaultRoute = _.get(launchOpts, "defaultRoute");
        let routeDets = this._router.getRouteDetails();
        if (routeDets.s && routeDets.app) {
            this._onRouteInfoChanged(null, {
                pathTokenDiff: {
                    s: {
                        oldValue: undefined,
                        newValue: routeDets.s,
                    },
                },
                newRouteDetails: routeDets,
            });
        } else {
            // If URL doesn't include /_app_/ and /_s_/, redirect to Device Manager
            const token = localStorage.getItem("Steve.session.token");
            const expireTokenTime = localStorage.getItem("Steve.session.expireTokenTime");

            // but only if a non expired token is available from the local storage
            if (token && expireTokenTime && !isNaN(expireTokenTime)) {
                const expireTime = parseInt(expireTokenTime, 10);
                const currentTime = Date.now(); // Current time in milliseconds

                if (expireTime > currentTime) {
                    this._router.goTo({
                        app: "GameConsole",
                        s: "GameCenter",
                        sln: "DeviceManager",
                    });
                    return;
                }
            }

            // Otherwise if default route is the login page, gets directToLogin to open either
            // SSO provider's login page or the Gluware login page
            if (defaultRoute) {
                if (defaultRoute.s === "LoginCenter" || defaultRoute.s === "LogoutCenter") {
                    location.href = `${window.origin}/session/directToLogin`;
                    return;
                }
                this._router.updateRouteInfo(defaultRoute);
            }
        }
    }

    _onRouteInfoChanged(rollupData, fullRouteInfo) {
        let originator = _.get(fullRouteInfo, "sourceDetails.originator", false);
        if (originator === this) {
            return;
        }

        const sectionName = _.get(fullRouteInfo, "newRouteDetails.s", false);
        if (
            sectionName === "LoginCenter" ||
            sectionName === "LogoutCenter" ||
            sectionName === "PasswordReset" ||
            sectionName === "Eula" ||
            sectionName === "Banner"
        ) {
            this._sectionRouterContainer.classList.add("login");
        } else {
            this._sectionRouterContainer.classList.remove("login");
        }

        if (_.get(fullRouteInfo, "newRouteDetails.s", false) === "RoboHelp") {
            document.querySelector("body").classList.add("robo-page");
        } else {
            document.querySelector("body").classList.remove("robo-page");
        }

        let processingHook = _.get(
            fullRouteInfo,
            "newRouteDetails.rd.processingHook.registeredDetails.name",
            false
        );
        let triggeredBy = _.get(fullRouteInfo, "newRouteDetails.rd.triggeredBy", false);
        //console.log(`routeChanged during processing of ${processingHook}, triggered by ${triggeredBy}`);
        if (processingHook && processingHook === triggeredBy) {
            fullRouteInfo.rd = _.omit(fullRouteInfo.rd, ["triggeredBy", "processingHook"]);
            this.processingChanges = false;
        }

        if (!this.processingChanges) {
            this.destinationRouteInfo = {rollupData, fullRouteInfo};
            this.processChanges();
        }
    }

    processChanges() {
        this.processingChanges = true;

        let fullRouteInfo = this.destinationRouteInfo.fullRouteInfo;
        //old_onRouteInfoChanged(rollupData, fullRouteInfo){
        //console.log("FSM routeInfo:");
        //console.log(fullRouteInfo);

        let srcSection = _.get(fullRouteInfo, "oldRouteDetails.s", "undefined");
        let destSection = _.get(fullRouteInfo, "newRouteDetails.s", "undefined");

        let originator = _.get(fullRouteInfo, "sourceDetails.originator");
        if (originator === this) {
            return;
        }

        this.noodlyAppendages = [];
        if (_.get(fullRouteInfo, "pathTokenDiff.s", false)) {
            let fromAppendages = this._di.getAllRegisteredByGlob(
                REGISTRATIONS.fromNamespace + srcSection + ".*"
            );
            let fromAnyAppendages = this._di.getAllRegisteredByGlob(
                REGISTRATIONS.fromNamespace + "any" + ".*"
            );
            fromAppendages = Object.assign({}, fromAppendages, fromAnyAppendages);
            if (!_.isEmpty(fromAppendages)) {
                let things = fromAppendages[REGISTRATIONS.fromNamespace + srcSection];
                if (things) {
                    things = Object.assign(
                        {},
                        things,
                        fromAnyAppendages[REGISTRATIONS.toNamespace + "any"]
                    );
                } else {
                    things = fromAnyAppendages[REGISTRATIONS.toNamespace + "any"];
                }
                _.forEach(things, (appendage) => {
                    this.addNoodlyAppendage(appendage);
                });
            }
            let toAppendages = this._di.getAllRegisteredByGlob(
                REGISTRATIONS.toNamespace + destSection + ".*"
            );
            let toAnyAppendages = this._di.getAllRegisteredByGlob(
                REGISTRATIONS.toNamespace + "any" + ".*"
            );
            toAppendages = Object.assign({}, toAppendages, toAnyAppendages);
            if (!_.isEmpty(toAppendages)) {
                let things = toAppendages[REGISTRATIONS.toNamespace + destSection];
                if (things) {
                    things = Object.assign(
                        {},
                        things,
                        toAppendages[REGISTRATIONS.toNamespace + "any"]
                    );
                } else {
                    things = toAppendages[REGISTRATIONS.toNamespace + "any"];
                }
                _.forEach(things, (appendage) => {
                    this.addNoodlyAppendage(appendage);
                });
            }
        }
        this.noodlyAppendages.push(() => {
            //console.log("triggering section change ", fullRouteInfo);
            this.updateRouteInfo({
                rd: {
                    processingHook: null,
                    triggeredBy: null,
                },
            });
            fullRouteInfo.rd = _.omit(fullRouteInfo.rd, ["triggeredBy", "processingHook"]);
            this._sectionRouterTokenChangeHandler(
                this.destinationRouteInfo.rollupData,
                fullRouteInfo
            );
            this.processingChanges = false;
        });

        let noodlePromiseEater = (allProm, noodleAppendage, currentIndex) => {
            //console.log(`1. checking for hook, idx:${currentIndex}`, noodleAppendage);

            if (this.noodlyAppendages.length === 0) {
                this._logger.warn(
                    `noodly appendage ${noodleAppendage.registeredDetails.name} triggered a route change, aborting execution of other appendages`
                );
                return false;
            }
            return allProm.then(() => {
                if (!this.processingChanges) {
                    return false;
                }
                if (
                    this.noodlyAppendages.length > 0 &&
                    currentIndex !== this.noodlyAppendages.length - 1
                ) {
                    this.updateRouteInfo({
                        rd: {
                            processingHook: {registeredDetails: noodleAppendage.registeredDetails},
                        },
                    });
                    let hookInstance = this._di.create(noodleAppendage.registeredObj);
                    //console.log("running hook startup()");
                    return hookInstance.startUp(fullRouteInfo);
                } else if (_.isFunction(noodleAppendage)) {
                    return noodleAppendage();
                } else {
                    return true;
                }
            });
        };

        this.serial(this.noodlyAppendages, noodlePromiseEater).catch((err) => {
            this._logger.warn({error: err}, "FSM serial processing interrupted");
        });
    }

    // create a passthrough for section router...  it will use this method to listen for section changes
    onPathTokenRollupChange(tokenNameArray, hdlr) {
        this._sectionRouterTokenChangeHandler = hdlr;
    }

    updateRouteInfo(routeInfo) {
        let opts = {originator: this};
        return this._router.updateRouteInfo(routeInfo, opts);
    }

    replaceRouteInfo(routeInfo) {
        let opts = {originator: this};
        return this._router.replaceRouteInfo(routeInfo, opts);
    }

    goTo(sectionName, addToHistory) {
        let opts = {originator: this};
        let routeDetails = {s: sectionName};
        if (addToHistory) {
            this._router.pushUpdatedRouteInfoHistory(routeDetails, opts);
        } else {
            this._router.updateRouteInfo(routeDetails, opts);
        }
    }

    startUp() {
        this._registerAllAppendages();
        this._registerListeners();
        document.body.appendChild(this._sectionRouterContainer);
        // return this._sectionRouter.initialize();
    }

    _routeToLogin() {
        let routeDetails = this._router.getRouteDetails();
        if (routeDetails) {
            localStorage.clear();
            if (routeDetails.s === "LoginCenter") {
                this._router.goTo({
                    app: "GameConsole",
                    s: "LoginCenter",
                    sln: null,
                    ra: "dashboard",
                });
            } else if (routeDetails.s === "LogoutCenter") {
                this._router.goTo({
                    app: "GameConsole",
                    s: "LogoutCenter",
                    sln: null,
                    ra: "dashboard",
                });
            } else {
                const token = this._userSession.token || localStorage["Steve.session.token"];
                localStorage.clear();
                location.href = `${window.origin}/session/logout/${token}`;
            }
        }
    }

    get processingChanges() {
        return this.getProcessingChanges();
    }

    set processingChanges(bool) {
        this.setProcessingChanges(bool);
    }

    getProcessingChanges() {
        return this._processingChanges;
    }

    setProcessingChanges(bool) {
        this._processingChanges = bool;
    }

    get destinationRouteInfo() {
        return this.getDestinationRouteInfo();
    }

    set destinationRouteInfo(obj) {
        this.setDestinationRouteInfo(obj);
    }

    getDestinationRouteInfo() {
        return this._destinationRouteInfo;
    }

    setDestinationRouteInfo(obj) {
        this._destinationRouteInfo = obj;
    }

    addNoodlyAppendage(appendage) {
        let appName = _.get(appendage, "registeredDetails.name", undefined);
        if (
            appName &&
            this.noodlyAppendages.find(
                (app) => _.get(app, "registeredDetails.name", false) === appName
            )
        ) {
            return;
        }
        this.noodlyAppendages.push(appendage);
    }

    get noodlyAppendages() {
        return this.getNoodlyAppendages();
    }

    set noodlyAppendages(array) {
        this.setNoodlyAppendages(array);
    }

    getNoodlyAppendages() {
        return this._noodlyAppendages;
    }

    setNoodlyAppendages(array) {
        this._noodlyAppendages = array;
    }

    serial(originalIterable, promiseReducer) {
        let attempted = {};
        let attemptedCount = 0;
        let proms = [];
        let completedPromises = [];
        let allPromise = originalIterable.reduce((allProm, resolveItOrNot, currentIndex) => {
            let result = promiseReducer(allProm, resolveItOrNot, currentIndex);
            if (result) {
                attemptedCount++;
                attempted[currentIndex] = true;
                allProm.then(
                    () => {
                        proms.push(result);
                    },
                    () => {}
                );
                result.then(
                    () => {
                        completedPromises.push(result);
                    },
                    () => {}
                );
                return result;
            } else {
                attempted[currentIndex] = false;
                return allProm;
            }
        }, Promise.resolve());

        return allPromise
            .then(() => {
                return Promise.resolve({
                    completedPromises: completedPromises,
                    completedCount: completedPromises.length,
                    attemptedCount: attemptedCount,
                    attemptedByIndex: attempted,
                });
            })
            .catch((e) => {
                let failedIndex = proms.length - 1;
                let failedIterable = originalIterable[failedIndex];
                return Promise.reject({
                    failure: e,
                    failedIndex: failedIndex,
                    failedIterable: failedIterable,
                    completedPromises: completedPromises,
                    completedCount: completedPromises.length,
                    attemptedCount: attemptedCount,
                    attemptedByIndex: attempted,
                });
            });
    }
}

module.exports = FlyingSpaghettiMonster;
module.exports.$inject = [
    "$teve.logger",
    "$teve.core",
    "$teve.di",
    "$teve.router",
    "$teve.userSession",
    "$teve.ui.userFeedback",
];
